<template>
  <Starfield :running="hasChrome" :bare="!hasChrome || gutters.bareStarfield" :class="{'q3po-bare-page': !hasChrome}">
    <iRow
      width="fill"
      :height="frameHeight"
      gap="none"
      wrap="nowrap"
      data-test="q3po-frame"
      :data-call-dock-side="dockSide"
    >
      <CallDockSlot v-if="frame.showSlots" side="left" />
      <iColumn
        width="fillv2"
        :height="frameHeight"
        gap="none"
        wrap="nowrap"
        :style-overrides="pageGutterOverrides"
        data-test="q3po-frame-main"
      >
        <iColumn
          width="fill"
          :height="frame.fillsViewport ? 'fill-remaining' : frameHeight"
          :gap="hasChrome ? 'standard' : 'none'"
          :wrap="frame.fillsViewport ? 'nowrap' : 'wrap'"
        >
          <iColumn
            width="fill"
            gap="none"
            no-shrink
            :min-width="0"
          >
            <AppHeader v-show="hasChrome" :current-path="currentPath" :root-booted="rootBooted">
              <template v-if="crumbSegments.length > 0" #sub-header>
                <span class="dp-crumbs">
                  <template v-for="(seg, idx) in crumbSegments" :key="idx">
                    <span class="dp-crumb-sep">/</span>
                    <EngineLink
                      v-if="seg.href && idx < crumbSegments.length - 1"
                      :url="seg.href"
                      :display-contents="false"
                      idle-class="dp-crumb-link"
                      active-class="dp-crumb-link"
                    >{{ seg.label }}</EngineLink>
                    <span v-else-if="idx < crumbSegments.length - 1">{{ seg.label }}</span>
                    <span v-else class="dp-crumb-current">{{ seg.label }}</span>
                  </template>
                </span>
              </template>
              <template v-if="showLegacyPanesToggle" #sub-header-end>
                <AppComponent
                  app-name="Q3po"
                  component-name="LegacyPanesToggle"
                  :contexts="contexts"
                />
              </template>
            </AppHeader>
            <CallDockSlot v-if="frame.showSlots" side="top" />
          </iColumn>
          <EntityPoll
            v-if="rootBooted && onQ3poApp && !onOfficeMap"
            :contexts="q3poConfigContexts"
            entity-context="configuration"
            action-name="stayInOffice"
            :interval-seconds="30"
            :pause-when-tab-hidden="false"
          />
          <AppComponent
            v-if="rootBooted"
            component-name="OfficeChromeComponent"
            app-name="Q3po"
            :contexts="officeChromeContexts"
          />
          <iColumn v-if="rootBooted && !onOfficeMap" width="fill" no-shrink>
            <AppComponent
              component-name="OfficeCallComponent"
              :contexts="officeChromeContexts"
            />
          </iColumn>
          <iColumn
            width="fill"
            :height="frame.pageColumnHeight"
            :overflow="frame.pageOverflow"
            gap="none"
            :wrap="frame.fillsViewport ? 'nowrap' : 'wrap'"
            data-test="q3po-page-column"
          >
            <Panel v-if="panelTitle" :title="panelTitle">
              <AppPage
                :app-name="appName"
                :page-name="pageName"
                :contexts="contexts"
                :entities="entities"
                :environment="environment"
                @page-chrome="onPageChrome"
              />
            </Panel>
            <AppPage
              v-else
              :app-name="appName"
              :page-name="pageName"
              :contexts="contexts"
              :entities="entities"
              :environment="environment"
              @page-chrome="onPageChrome"
            />
          </iColumn>
        </iColumn>
        <CallDockSlot v-if="frame.showSlots" side="bottom" />
      </iColumn>
      <CallDockSlot v-if="frame.showSlots" side="right" />
    </iRow>
  </Starfield>
</template>

<script>
import App from "@bloglovin/inspire-engine/App";
import AppPage from "@bloglovin/inspire-engine/Pages/AppPage";
import EngineLink from "@bloglovin/inspire-engine/Components/EngineLink";
import EntityPoll from "@bloglovin/inspire-engine/Components/EntityPoll";
import AppComponent from "@bloglovin/inspire-engine/Components/AppComponent";
import CallDockSlot from "@bloglovin/inspire-engine/Components/CallDockSlot";
import { activeDockSide } from "@bloglovin/inspire-engine/Helpers/callSession";
import { frameLayoutFor, sideDockGutters } from "@/helpers/callDockFrame.mjs";
import { ensureViewportBound, viewportWidth } from "@bloglovin/inspire-engine/DynamicComponents/Helpers/viewport";
import { isProjectDetailsPath, isWideViewport } from "+/Q3po/Components/projectDetailsLayout.mjs";
import { postOfficeHeartbeat, OFFICE_HEARTBEAT_MS } from "@/helpers/officeHeartbeat.mjs";
import { METAVERSE_APP, officeChromeContexts } from "@/helpers/office-chrome.mjs";
import Starfield from "@/components/Starfield";
import AppHeader from "@/components/AppHeader";
import Panel from "@/components/Panel";
import { useStyleStore } from "@vcl";
import { applyQ3poTheme } from "@/themes/apply-q3po-theme";
import { crumbSegments as buildCrumbSegments } from "@/helpers/crumbs.mjs";
import { BOOT_STALL_MS, collectBootFacts, watchBootStall } from "@/helpers/bootStallReport.mjs";
import { PAGE_CHROME_DEFAULT, PAGE_CHROME_NONE, PAGE_CHROME_VALUES } from "@bloglovin/inspire-engine/Helpers/pageChrome";

export default {
  name: "Q3poAppPage",
  components: { Starfield, AppHeader, AppPage, Panel, EngineLink, EntityPoll, AppComponent, CallDockSlot },
  props: {
    appName: {
      type: String,
      required: true,
    },
    pageName: {
      type: String,
      required: false,
      default: "",
    },
    contexts: {
      type: Object,
      required: true,
    },
    entities: {
      type: Object,
      required: false,
      default: () => ({}),
    },
    environment: {
      type: Object,
      required: false,
      default: () => ({}),
    },
    panelTitle: {
      type: String,
      required: false,
      default: "",
    },
    pageChrome: {
      type: String,
      required: false,
      default: PAGE_CHROME_DEFAULT,
      validator: value => PAGE_CHROME_VALUES.includes(value),
    },
  },
  data() {
    return {
      currentPath: typeof window !== "undefined" ? window.location.pathname : "",
      chrome: this.pageChrome,
      rootBooted: false,
    };
  },
  computed: {
    hasChrome() {
      return this.chrome !== PAGE_CHROME_NONE;
    },
    dockSide() {
      return this.hasChrome ? activeDockSide(viewportWidth.value) : "";
    },
    frame() {
      return frameLayoutFor({ chrome: this.chrome, dockSide: this.dockSide });
    },
    gutters() {
      return sideDockGutters({ dockSide: this.dockSide, viewportWidth: viewportWidth.value });
    },
    pageGutterOverrides() {
      return this.gutters.pagePadding ? { paddingLeft: this.gutters.pagePadding, paddingRight: this.gutters.pagePadding } : {};
    },
    frameHeight() {
      return this.hasChrome && !this.frame.fillsViewport ? undefined : "fill";
    },
    showLegacyPanesToggle() {
      return this.rootBooted && this.onQ3poApp && isProjectDetailsPath(this.currentPath) && isWideViewport(viewportWidth.value);
    },
    onQ3poApp() {
      return this.appName === "Q3po";
    },
    onOfficeMap() {
      return /^\/office(\/\d+)?\/?$/.test(this.currentPath);
    },
    q3poConfigContexts() {
      return { configuration: { entityType: "Configuration", pk: App.getRoot().getCurrentConfigurationPk(), appName: "Q3po" } };
    },
    officeChromeContexts() {
      const root = App.getRoot();
      return officeChromeContexts(this.contexts, root.getDependencyConfigurationId(METAVERSE_APP), root.getCurrentConfigurationPk());
    },
    crumbSegments() {
      if (!this.rootBooted) {
        return buildCrumbSegments(this.currentPath, () => null);
      }
      const root = App.getRoot();
      return buildCrumbSegments(this.currentPath, path => root.findMatchingPage(path));
    },
  },
  watch: {
    hasChrome: {
      immediate: true,
      handler(hasChrome) {
        applyQ3poTheme(useStyleStore(), { workspace: !hasChrome });
      },
    },
  },
  created() {
    ensureViewportBound();
  },
  mounted() {
    if (!this.onQ3poApp) {
      this.sendOfficeHeartbeat();
      this.officeHeartbeatTimer = window.setInterval(this.sendOfficeHeartbeat, OFFICE_HEARTBEAT_MS);
    }
    window.addEventListener("inspire:navigate", this.syncCurrentPath);
    window.addEventListener("popstate", this.syncCurrentPath);
    this.cancelBootStallWatch = watchBootStall({
      timeoutMs: BOOT_STALL_MS,
      isSettled: () => this.rootBooted,
      collect: () => collectBootFacts({
        appName: this.appName,
        path: window.location.pathname,
        elapsedMs: BOOT_STALL_MS,
        rootBooted: App.isRootBooted(),
        navigatorLike: typeof navigator === "undefined" ? null : navigator,
        documentLike: typeof document === "undefined" ? null : document,
        indexedDbLike: typeof indexedDB === "undefined" ? null : indexedDB,
      }),
      report: report => console.error(report.message, report.facts), // eslint-disable-line no-console
    });
  },
  beforeUnmount() {
    this.cancelBootStallWatch();
    if (this.officeHeartbeatTimer) {
      window.clearInterval(this.officeHeartbeatTimer);
    }
    applyQ3poTheme(useStyleStore());
    window.removeEventListener("inspire:navigate", this.syncCurrentPath);
    window.removeEventListener("popstate", this.syncCurrentPath);
  },
  methods: {
    sendOfficeHeartbeat() {
      postOfficeHeartbeat().catch(() => {});
    },
    syncCurrentPath() {
      this.currentPath = window.location.pathname;
    },
    onPageChrome(chrome) {
      this.chrome = chrome;
      this.rootBooted = App.isRootBooted();
    },
  },
};
</script>

<style scoped>
.q3po-bare-page {
  --q3po-font-floor: 16px;
}

.q3po-bare-page :deep(button),
.q3po-bare-page :deep(input),
.q3po-bare-page :deep(select),
.q3po-bare-page :deep(textarea),
.q3po-bare-page :deep(code),
.q3po-bare-page :deep(pre),
.q3po-bare-page :deep(kbd),
.q3po-bare-page :deep(samp) {
  font-size: max(1em, var(--q3po-font-floor));
}

.dp-crumbs {
  display: inline-flex;
  align-items: baseline;
  gap: 8px;
  font-family: 'Starjedi', 'TradeGothic', sans-serif;
  font-weight: bold;
  font-size: 12px;
  line-height: 1;
  color: #c5c5cc;
}

.dp-crumb-sep {
  color: #7a7a85;
}

:deep(.dp-crumb-link) {
  color: yellow;
  text-decoration: none;
}

:deep(.dp-crumb-link:hover),
:deep(.dp-crumb-link:focus-visible) {
  text-decoration: underline;
  outline: none;
}

.dp-crumb-current {
  color: #f5f5f5;
}
</style>
